import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { acceptInvite, parseInviteError } from '@/lib/teamInvites';
import { getInviteRedirectPath, INVITE_JUST_ACCEPTED_KEY } from '@/lib/inviteRedirect';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

type AcceptInviteState = 'loading' | 'checking-auth' | 'accepting' | 'success' | 'error';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { setActiveWorkspace, refetchWorkspaces } = useWorkspaceScope();

  const [state, setState] = useState<AcceptInviteState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const acceptStartedRef = useRef(false);
  const cancelledRef = useRef(false);

  const token = searchParams.get('token');
  const pendingToken = localStorage.getItem('pending_invite_token') || token;

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!token && !pendingToken) {
      setState('error');
      setError('Invalid invite link. Please check the link or request a new invitation.');
      return;
    }

    if (authLoading) return;

    if (!user) {
      if (token) localStorage.setItem('pending_invite_token', token);
      const returnPath = getInviteRedirectPath(token ?? pendingToken);
      navigate(`/auth?redirect=${encodeURIComponent(returnPath)}`);
      return;
    }

    if (user && pendingToken && !acceptStartedRef.current) {
      acceptStartedRef.current = true;
      acceptInvitation(pendingToken);
    }
  }, [token, pendingToken, user, authLoading, navigate]);

  const acceptInvitation = async (inviteToken: string) => {
    try {
      setState('accepting');
      setError(null);

      const newWorkspaceId = await acceptInvite(inviteToken);
      if (cancelledRef.current) return;

      localStorage.removeItem('pending_invite_token');
      setWorkspaceId(newWorkspaceId);
      setActiveWorkspace(newWorkspaceId);
      await refetchWorkspaces();
      if (cancelledRef.current) return;

      setState('success');
      sessionStorage.setItem(INVITE_JUST_ACCEPTED_KEY, 'true');
      setTimeout(() => {
        if (!cancelledRef.current) navigate('/app');
      }, 1500);
    } catch (err) {
      if (cancelledRef.current) return;
      const isAlreadyUsed = isInviteAlreadyUsedError(err);
      if (isAlreadyUsed) {
        localStorage.removeItem('pending_invite_token');
        setState('success');
        sessionStorage.setItem(INVITE_JUST_ACCEPTED_KEY, 'true');
        setTimeout(() => {
          if (!cancelledRef.current) navigate('/app');
        }, 1500);
        return;
      }
      setError(parseInviteError(err));
      setState('error');
    }
  };

  if (state === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <h1 className="text-xl font-semibold">Joining workspace…</h1>
          <p className="text-muted-foreground text-sm">Sign in to accept your invitation.</p>
        </div>
      </div>
    );
  }

  if (state === 'accepting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <h1 className="text-xl font-semibold">Joining workspace…</h1>
          <p className="text-muted-foreground text-sm">Please wait while we add you to the workspace.</p>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold">Invite accepted</h1>
            <p className="text-muted-foreground text-sm">Redirecting…</p>
          </div>
          <Button onClick={() => navigate('/app')} className="mt-4">
            Go to workspace
          </Button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md mx-auto px-4 space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold">Invalid invite link</h1>
          </div>

          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to accept invitation</AlertTitle>
            <AlertDescription className="mt-2">
              {error || 'This link may be expired or invalid. Request a new invitation from your team admin.'}
            </AlertDescription>
          </Alert>

          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate('/auth')} variant="default">
              Sign in
            </Button>
            <Button onClick={() => navigate('/')} variant="outline">
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/** Robust detection: stable code, 409, or precise message match. Treat as idempotent success. */
function isInviteAlreadyUsedError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? '';
  if (msg === 'ALREADY_MEMBER') return true;
  const code = (err as { code?: string }).code;
  if (code === '409' || code === 'ALREADY_MEMBER') return true;
  return /already\s+(a\s+)?member|invite\s+already\s+(used|accepted)/i.test(msg);
}
